(() => {
  const config = window.UpsellPopupConfig || {};
  if (!config.enabled) return;

  const popup = document.querySelector('[data-upsell-popup]');
  if (!popup) return;

  const refs = {
    heading: popup.querySelector('[data-upsell-heading]'),
    added: popup.querySelector('[data-upsell-added]'),
    addedImage: popup.querySelector('[data-upsell-added-image]'),
    addedTitle: popup.querySelector('[data-upsell-added-title]'),
    addedPrice: popup.querySelector('[data-upsell-added-price]'),
    product: popup.querySelector('[data-upsell-product]'),
    productImage: popup.querySelector('[data-upsell-product-image]'),
    productTitle: popup.querySelector('[data-upsell-product-title]'),
    productPrice: popup.querySelector('[data-upsell-product-price]'),
    productDescription: popup.querySelector('[data-upsell-product-description]'),
    form: popup.querySelector('[data-upsell-form]'),
    variantId: popup.querySelector('[data-upsell-variant-id]'),
    addButton: popup.querySelector('[data-upsell-add]'),
    continueButton: popup.querySelector('[data-upsell-continue]'),
    closeButtons: popup.querySelectorAll('[data-upsell-close]')
  };

  const state = {
    isOpen: false,
    isHandling: false,
    suppressNext: false,
    lastVariantId: null,
    lastHandledAt: 0,
    collectionCache: new Map(),
    productCache: new Map()
  };

  const moneyFormat = window.theme && window.theme.moneyFormat ? window.theme.moneyFormat : '${{amount}}';

  function formatMoney(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents, moneyFormat);
    }
    return `£${(cents / 100).toFixed(2)}`;
  }

  function normalizeValue(value) {
    return (value || '').trim().toLowerCase();
  }

  function getTriggerHandles() {
    return (config.triggerValue || '')
      .split(',')
      .map((value) => normalizeValue(value))
      .filter(Boolean);
  }

  function openPopup() {
    if (state.isOpen) return;
    popup.hidden = false;
    document.body.style.overflow = 'hidden';
    state.isOpen = true;
    refs.continueButton?.focus();
  }

  function closePopup() {
    if (!state.isOpen) return;
    popup.hidden = true;
    document.body.style.overflow = '';
    state.isOpen = false;
  }

  refs.closeButtons.forEach((btn) => btn.addEventListener('click', closePopup));
  refs.continueButton?.addEventListener('click', closePopup);

  function getAddUrl(input) {
    if (!input) return '';
    if (typeof input === 'string') return input;
    if (input.url) return input.url;
    return '';
  }

  function isAddToCartUrl(url) {
    return /\/cart\/add(\.js)?/i.test(url);
  }

  async function getCart() {
    const response = await fetch('/cart.js', { credentials: 'same-origin' });
    if (!response.ok) throw new Error('Failed to fetch cart');
    return response.json();
  }

  async function getProduct(handle) {
    if (!handle) return null;
    if (state.productCache.has(handle)) return state.productCache.get(handle);
    const response = await fetch(`/products/${handle}.js`, { credentials: 'same-origin' });
    if (!response.ok) return null;
    const product = await response.json();
    state.productCache.set(handle, product);
    return product;
  }

  async function getCollectionHandles(handle) {
    if (!handle) return new Set();
    if (state.collectionCache.has(handle)) return state.collectionCache.get(handle);

    const handles = new Set();
    const limit = 250;
    let page = 1;

    while (true) {
      const response = await fetch(`/collections/${handle}/products.json?limit=${limit}&page=${page}`, {
        credentials: 'same-origin'
      });
      if (!response.ok) break;
      const data = await response.json();
      const products = data.products || [];
      products.forEach((product) => handles.add(product.handle));
      if (products.length < limit) break;
      page += 1;
    }

    state.collectionCache.set(handle, handles);
    return handles;
  }

  async function shouldTriggerForItem(item) {
    const triggerHandles = getTriggerHandles();
    if (!triggerHandles.length) return false;

    for (const handle of triggerHandles) {
      const handles = await getCollectionHandles(handle);
      if (handles.has(item.handle)) return true;
    }

    return false;
  }

  function renderAddedItem(item) {
    if (!item) {
      refs.added.hidden = true;
      return;
    }
    refs.added.hidden = false;
    refs.addedTitle.textContent = item.product_title || item.title || '';
    if (refs.addedPrice) {
      const price = item.final_price != null ? item.final_price : item.price;
      refs.addedPrice.textContent = price != null ? formatMoney(price) : '';
    }
    if (refs.addedImage) {
      refs.addedImage.src = item.image || item.featured_image || '';
      refs.addedImage.alt = item.product_title || item.title || '';
    }
  }

  function pickVariant(product) {
    if (!product || !product.variants || !product.variants.length) return null;
    return product.variants.find((variant) => variant.available) || product.variants[0];
  }

  function renderUpsell(product) {
    if (!product) return false;
    const variant = pickVariant(product);
    if (!variant) return false;

    refs.productTitle.textContent = product.title || '';
    refs.productPrice.textContent = formatMoney(variant.price);
    refs.productDescription.innerHTML = product.description || '';

    if (refs.productImage) {
      const image = product.images && product.images.length ? product.images[0] : null;
      refs.productImage.src = image || '';
      refs.productImage.alt = product.title || '';
    }

    refs.variantId.value = variant.id;
    refs.addButton.textContent = config.addLabel || 'Add to cart';
    refs.continueButton.textContent = config.continueLabel || 'Continue shopping';

    return true;
  }

  async function showUpsell(variantId, responseItem) {
    if (!config.upsellProductHandle) return;

    if (state.isHandling) return;
    state.isHandling = true;

    try {
      const cart = await getCart();
      const addedItem = cart.items.find((item) => item.variant_id === variantId) || responseItem;
      if (!addedItem || !addedItem.handle) return;

      const upsellProduct = await getProduct(config.upsellProductHandle);
      if (!upsellProduct) return;

      const upsellAlreadyInCart = cart.items.some((item) => {
        if (config.upsellProductId && item.product_id === config.upsellProductId) return true;
        return item.handle === config.upsellProductHandle;
      });
      if (upsellAlreadyInCart) return;

      const shouldTrigger = await shouldTriggerForItem(addedItem);
      if (!shouldTrigger) return;

      refs.heading.textContent = config.heading || 'You might also like';
      renderAddedItem(addedItem);
      if (renderUpsell(upsellProduct)) {
        openPopup();
      }
    } catch (error) {
      console.warn('[Upsell Popup] Failed to show popup', error);
    } finally {
      state.isHandling = false;
    }
  }

  function handleAddResponse(data) {
    if (!data || state.suppressNext) {
      state.suppressNext = false;
      return;
    }
    const variantId = data.variant_id || data.id;
    if (!variantId) return;

    const now = Date.now();
    if (state.lastVariantId === variantId && now - state.lastHandledAt < 500) return;
    state.lastVariantId = variantId;
    state.lastHandledAt = now;

    showUpsell(variantId, data);
  }

  function interceptFetch() {
    const originalFetch = window.fetch;
    if (!originalFetch) return;

    window.fetch = async (...args) => {
      const url = getAddUrl(args[0]);
      const response = await originalFetch(...args);

      if (isAddToCartUrl(url)) {
        try {
          const cloned = response.clone();
          cloned.json().then(handleAddResponse).catch(() => {});
        } catch (error) {
          console.warn('[Upsell Popup] Failed to read add-to-cart response', error);
        }
      }

      return response;
    };
  }

  function interceptXHR() {
    const originalOpen = XMLHttpRequest.prototype.open;
    const originalSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function (method, url, ...rest) {
      this.__upsellUrl = url;
      return originalOpen.call(this, method, url, ...rest);
    };

    XMLHttpRequest.prototype.send = function (body) {
      if (isAddToCartUrl(this.__upsellUrl)) {
        this.addEventListener('load', () => {
          if (this.status >= 200 && this.status < 300) {
            try {
              const data = JSON.parse(this.responseText);
              handleAddResponse(data);
            } catch (_) {
              // no-op
            }
          }
        });
      }

      return originalSend.call(this, body);
    };
  }

  if (refs.form) {
    refs.form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (!refs.variantId.value) return;

      const formData = new FormData(refs.form);
      const button = refs.addButton;
      const cartDrawer = document.querySelector('cart-drawer');

      if (button) {
        button.disabled = true;
        button.classList.add('loading');
      }

      if (cartDrawer) {
        const sections = cartDrawer.getSectionsToRender().map((section) => section.id);
        formData.append('sections', sections);
        formData.append('sections_url', window.location.pathname);
      }

      state.suppressNext = true;

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          body: formData,
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
          credentials: 'same-origin'
        });
        if (!response.ok) throw new Error('Upsell add failed');
        const responseData = await response.json();

        if (cartDrawer && responseData.sections) {
          cartDrawer.renderContents(responseData);
        } else if (typeof window.refreshCartDrawerAfterRebuy === 'function') {
          window.refreshCartDrawerAfterRebuy().catch(() => {});
        }

        if (window.publish && window.PUB_SUB_EVENTS) {
          window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'upsell-popup' });
        }

        closePopup();

      } catch (error) {
        console.warn('[Upsell Popup] Add to cart failed', error);
      } finally {
        if (button) {
          button.disabled = false;
          button.classList.remove('loading');
        }
      }
    });
  }

  interceptFetch();
  interceptXHR();
})();
